import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Button, EmptyState, Icon, PageHeader, Pill, Select, Skeleton, Tabs, type IconName } from "@astrum/ui";
import { api, type Channel, type Person, type Project } from "../lib/api.js";
import {
  KIND_LABEL,
  filtersFromParams,
  filtersToParams,
  hitHref,
  moveActive,
  segmentsText,
  type HighlightSegment,
  type SearchFilters,
  type SearchHit,
  type SearchKind,
  type SearchResponse,
} from "../lib/search-results.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./search.css";

const KIND_ICON: Record<SearchKind, IconName> = {
  message: "chat",
  task: "checklist",
  project: "work",
  file: "document",
  post: "home",
  doc: "document",
};

const TABS = [
  { id: "all", label: "All" },
  ...(Object.keys(KIND_LABEL) as SearchKind[]).map((kind) => ({ id: kind, label: KIND_LABEL[kind] })),
];

const SEARCH_DEBOUNCE_MS = 250;
const PAGE_SIZE = 20;

/** Matched terms are wrapped in <mark>; the text itself is never parsed as HTML. */
function Highlighted({ segments }: { segments: HighlightSegment[] | null }) {
  if (!segments) return null;
  return (
    <>
      {segments.map((s, i) => (s.hit ? <mark key={i}>{s.text}</mark> : <Fragment key={i}>{s.text}</Fragment>))}
    </>
  );
}

function context(hit: SearchHit): string {
  const parts: string[] = [];
  if (hit.kind === "message" && hit.channel) parts.push(`#${hit.channel.name ?? "channel"}`);
  else if (hit.project && hit.kind !== "project") parts.push(hit.project.code ?? hit.project.name ?? "");
  if (hit.author?.displayName) parts.push(hit.author.displayName);
  parts.push(new Date(hit.createdAt).toLocaleDateString());
  return parts.filter(Boolean).join(" · ");
}

export function Search() {
  usePageTitle("Search");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(params), [params]);

  // The input is edited locally and pushed to the URL after a pause, so typing
  // does not fire a request per keystroke.
  const [draft, setDraft] = useState(filters.q);
  const inputRef = useRef<HTMLInputElement>(null);

  const [hits, setHits] = useState<SearchHit[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(-1);

  const [people, setPeople] = useState<Person[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    inputRef.current?.focus();
    void Promise.allSettled([
      api.get<{ people: Person[] }>("/people"),
      api.get<{ channels: Channel[] }>("/channels"),
      api.get<{ projects: Project[] }>("/projects"),
    ]).then(([p, c, pr]) => {
      if (p.status === "fulfilled") setPeople(p.value.people);
      if (c.status === "fulfilled") setChannels(c.value.channels.filter((ch) => ch.kind !== "direct"));
      if (pr.status === "fulfilled") setProjects(pr.value.projects);
    });
  }, []);

  const update = useCallback(
    (patch: Partial<SearchFilters>) => {
      setParams(filtersToParams({ ...filters, ...patch }), { replace: true });
    },
    [filters, setParams],
  );

  // Draft -> URL.
  useEffect(() => {
    if (draft.trim() === filters.q.trim()) return;
    const timer = setTimeout(() => update({ q: draft }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, filters.q, update]);

  // The URL is the query: a change of any filter restarts from page one.
  const queryString = useMemo(() => {
    const p = filtersToParams(filters);
    p.set("limit", String(PAGE_SIZE));
    return p.toString();
  }, [filters]);

  useEffect(() => {
    setActive(-1);
    if (!filters.q.trim()) {
      setHits([]);
      setNextCursor(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<SearchResponse>(`/search/results?${queryString}`)
      .then((res) => {
        if (cancelled) return;
        setHits(res.results);
        setNextCursor(res.nextCursor);
      })
      .catch(() => {
        if (!cancelled) setError("Search failed. Try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [queryString, filters.q]);

  const loadMore = useCallback(() => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    api
      .get<SearchResponse>(`/search/results?${queryString}&cursor=${encodeURIComponent(nextCursor)}`)
      .then((res) => {
        setHits((prev) => [...prev, ...res.results]);
        setNextCursor(res.nextCursor);
      })
      .catch(() => setError("Could not load more results."))
      .finally(() => setLoadingMore(false));
  }, [nextCursor, loadingMore, queryString]);

  const open = useCallback((hit: SearchHit) => navigate(hitHref(hit)), [navigate]);

  // Arrow keys move through results from the input (or the list); Enter opens.
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setActive((cur) => moveActive(cur, event.key === "ArrowDown" ? 1 : -1, hits.length));
    } else if (event.key === "Enter" && active >= 0 && hits[active]) {
      event.preventDefault();
      open(hits[active]!);
    } else if (event.key === "Escape" && draft) {
      setDraft("");
      update({ q: "" });
    }
  };

  useEffect(() => {
    if (active < 0) return;
    document.getElementById(`search-hit-${hits[active]?.id}`)?.scrollIntoView({ block: "nearest" });
  }, [active, hits]);

  // "/" jumps back to the box, as in most search UIs.
  useEffect(() => {
    function onSlash(event: globalThis.KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (event.key === "/" && target?.tagName !== "INPUT" && target?.tagName !== "SELECT") {
        event.preventDefault();
        inputRef.current?.focus();
      }
    }
    document.addEventListener("keydown", onSlash);
    return () => document.removeEventListener("keydown", onSlash);
  }, []);

  const chips: { key: keyof SearchFilters; label: string }[] = [];
  if (filters.person) chips.push({ key: "person", label: `From ${people.find((p) => p.id === filters.person)?.displayName ?? "person"}` });
  if (filters.channel) chips.push({ key: "channel", label: `In #${channels.find((c) => c.id === filters.channel)?.name ?? "channel"}` });
  if (filters.project) chips.push({ key: "project", label: `Project ${projects.find((p) => p.id === filters.project)?.code ?? ""}`.trim() });
  if (filters.from) chips.push({ key: "from", label: `After ${filters.from}` });
  if (filters.to) chips.push({ key: "to", label: `Before ${filters.to}` });
  if (filters.hasFile) chips.push({ key: "hasFile", label: "Has file" });

  return (
    <div className="search-page">
      <PageHeader title="Search" />
      <div className="search-body" onKeyDown={onKeyDown}>
        <div className="search-box">
          <Icon name="search" size={16} />
          <input
            ref={inputRef}
            className="search-input"
            type="search"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Search messages, tasks, projects, files and posts…"
            aria-label="Search"
            aria-controls="search-results"
            aria-activedescendant={hits[active] ? `search-hit-${hits[active]!.id}` : undefined}
            autoComplete="off"
          />
        </div>

        <Tabs
          variant="pill"
          tabs={TABS}
          value={filters.type || "all"}
          onChange={(id) => update({ type: id === "all" ? "" : (id as SearchKind), ...(id !== "all" && id !== "message" ? { hasFile: false } : {}) })}
        />

        <div className="search-filters">
          <Select aria-label="Person" value={filters.person} onChange={(e) => update({ person: e.target.value })}>
            <option value="">Anyone</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.displayName}
              </option>
            ))}
          </Select>
          <Select aria-label="Channel" value={filters.channel} onChange={(e) => update({ channel: e.target.value })}>
            <option value="">Any channel</option>
            {channels.map((c) => (
              <option key={c.id} value={c.id}>
                #{c.name}
              </option>
            ))}
          </Select>
          <Select aria-label="Project" value={filters.project} onChange={(e) => update({ project: e.target.value })}>
            <option value="">Any project</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} · {p.name}
              </option>
            ))}
          </Select>
          <label className="search-date">
            <span>From</span>
            <input type="date" value={filters.from} max={filters.to || undefined} onChange={(e) => update({ from: e.target.value })} />
          </label>
          <label className="search-date">
            <span>To</span>
            <input type="date" value={filters.to} min={filters.from || undefined} onChange={(e) => update({ to: e.target.value })} />
          </label>
          <label className="search-check">
            <input
              type="checkbox"
              checked={filters.hasFile}
              onChange={(e) => update({ hasFile: e.target.checked, ...(e.target.checked ? { type: "message" as const } : {}) })}
            />
            Has file
          </label>
        </div>

        {chips.length > 0 && (
          <div className="search-chips" aria-label="Active filters">
            {chips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                className="search-chip"
                onClick={() => update({ [chip.key]: chip.key === "hasFile" ? false : "" } as Partial<SearchFilters>)}
                aria-label={`Remove filter: ${chip.label}`}
              >
                {chip.label}
                <Icon name="close" size={12} />
              </button>
            ))}
          </div>
        )}

        <div className="search-results" aria-busy={loading}>
          {error && <p className="search-error" role="alert">{error}</p>}
          {loading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div className="search-hit search-hit--skeleton" key={i}>
                <Skeleton height={14} width="40%" />
                <Skeleton height={12} width="85%" />
              </div>
            ))}
          {!loading && !error && !filters.q.trim() && (
            <EmptyState title="Search everything you can see" description="Try a name, a project code, or a phrase in quotes. Use - to leave a word out." />
          )}
          {!loading && !error && filters.q.trim() && hits.length === 0 && (
            <EmptyState title="No results" description="Try fewer words, or remove a filter." />
          )}
          {/* The listbox holds only options; skeletons, empty states and "Load more" sit outside it. */}
          <div id="search-results" role="listbox" aria-label="Search results" className="search-results__hits">
          {!loading &&
            hits.map((hit, index) => (
              <button
                key={`${hit.kind}-${hit.id}`}
                id={`search-hit-${hit.id}`}
                type="button"
                role="option"
                aria-selected={index === active}
                className={`search-hit ${index === active ? "is-active" : ""}`}
                onMouseEnter={() => setActive(index)}
                onClick={() => open(hit)}
              >
                <span className="search-hit__icon">
                  <Icon name={KIND_ICON[hit.kind]} size={16} />
                </span>
                <span className="search-hit__main">
                  <span className="search-hit__title">
                    {hit.title ? <Highlighted segments={hit.title} /> : <span className="search-hit__kind">{KIND_LABEL[hit.kind].replace(/s$/, "")}</span>}
                    {hit.kind === "task" && hit.meta && <Pill tone="neutral">{hit.meta.replace("_", " ")}</Pill>}
                  </span>
                  {hit.snippet && segmentsText(hit.snippet).trim() && (
                    <span className="search-hit__snippet">
                      <Highlighted segments={hit.snippet} />
                    </span>
                  )}
                  <span className="search-hit__meta">{context(hit)}</span>
                </span>
              </button>
            ))}
          </div>
          {!loading && nextCursor && (
            <div className="search-more">
              <Button variant="secondary" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "Loading…" : "Show more"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
